/**
 * Play animation — a pure function of (play, t) → play.
 *
 * The whole design rests on one decision: animating does NOT get its own
 * renderer. `playAtTime` returns an ordinary PlayData with the players moved
 * along their paths and the lines trimmed to how far they have been run, and
 * the existing canvas renderer and SVG builder draw it exactly as they draw
 * anything else. So there is no second drawing implementation to drift out of
 * sync with the one the golden test pins.
 *
 * Two consequences worth knowing:
 *   - Nothing here mutates its input. Callers animate straight off the stored
 *     play; no copy is made for them and none is needed.
 *   - No play has to be edited to animate. Which player runs which line is
 *     inferred from the drawing (nearest player to the line's start), and the
 *     timing is derived from path length. `delay` / `dur` on a line override
 *     the derived values when someone wants to tune one, and every play
 *     imported from the plugin animates without either.
 */
import type { PlayData, PlayLine, PlayPlayer } from '../types';
import { pbIsStaticProp } from './constants';
import type { XY } from './geometry';

/** Normalised units (fraction of the canvas) travelled per second. */
export const ANIM_SPEED = 0.42;
/** No segment is snappier than this, however short the path. */
export const ANIM_MIN_DUR = 260;
/** Pre-snap motion is walked, not sprinted. */
export const ANIM_MOTION_SPEED = 0.26;
/** A line's start point must be within this (normalised) distance of a player to bind to them. */
export const ANIM_BIND_RADIUS = 0.13;
/** Two line endpoints this close are treated as the same point, so the lines chain. */
export const ANIM_JOIN_RADIUS = 0.05;
/** Held on the last frame before a loop restarts. */
export const ANIM_TAIL_MS = 700;
/** The finished diagram is shown this long before anyone moves… */
export const ANIM_HOLD_MS = 1000;
/** …then its paths fade out over this long as the players start running. */
export const ANIM_FADE_MS = 400;

export interface AnimSegment {
  /** Index into play.lines. */
  line: number;
  /** Index into play.players, or -1 when no player sits at this line's start. */
  actor: number;
  /** Milliseconds from the start of the animation (0 = earliest motion). */
  start: number;
  /** Milliseconds to travel it. */
  dur: number;
  /** Runs before the snap. */
  preSnap: boolean;
}

export interface Timeline {
  segments: AnimSegment[];
  /** When the ball is snapped, in ms from the start. 0 when nothing is in motion. */
  snap: number;
  /** Last frame of movement, in ms. */
  duration: number;
}

// ─── Polyline maths ─────────────────────────────────────────────────────────

/** Total length of a polyline in normalised units. */
export function polylineLength(pts: readonly XY[]): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i]!.x - pts[i - 1]!.x,
      dy = pts[i]!.y - pts[i - 1]!.y;
    total += Math.sqrt(dx * dx + dy * dy);
  }
  return total;
}

/** The point `d` units along the polyline, clamped to both ends. */
export function pointAtDistance(pts: readonly XY[], d: number): XY {
  if (pts.length === 0) return { x: 0, y: 0 };
  if (pts.length === 1 || d <= 0) return { x: pts[0]!.x, y: pts[0]!.y };
  let rem = d;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!,
      b = pts[i]!;
    const dx = b.x - a.x,
      dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) continue;
    if (rem <= len) {
      const t = rem / len;
      return { x: a.x + dx * t, y: a.y + dy * t };
    }
    rem -= len;
  }
  const last = pts[pts.length - 1]!;
  return { x: last.x, y: last.y };
}

/**
 * The first `d` units of a polyline, with a synthesised final point at exactly
 * `d`. Returns fewer than 2 points when nothing has been travelled yet — which
 * is how a not-yet-run route stays invisible: both renderers skip a line with
 * under two points.
 */
export function trimPolyline(pts: readonly XY[], d: number): XY[] {
  if (pts.length < 2 || d <= 0) return [];
  const out: XY[] = [{ x: pts[0]!.x, y: pts[0]!.y }];
  let rem = d;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!,
      b = pts[i]!;
    const dx = b.x - a.x,
      dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) continue;
    if (rem <= len) {
      const t = rem / len;
      out.push({ x: a.x + dx * t, y: a.y + dy * t });
      return out;
    }
    out.push({ x: b.x, y: b.y });
    rem -= len;
  }
  return out;
}

// ─── Binding lines to the players who run them ──────────────────────────────

/**
 * Index of the player nearest a line's first point, or -1.
 *
 * Deliberately inferred rather than stored. A stored binding would have to name
 * a player, players have no id, and binding by array index is precisely the bug
 * that made the plugin stamp ids onto the wrong play in v2.31.0. Inference has
 * no such failure mode: it is recomputed from the drawing every time, it is
 * right whenever the diagram reads correctly to a human, and it costs existing
 * plays nothing.
 */
export function lineActor(play: PlayData, lineIdx: number): number {
  const line = play.lines[lineIdx];
  if (!line || !line.points || line.points.length < 2) return -1;
  const start = line.points[0]!;
  let best = -1,
    bestD = ANIM_BIND_RADIUS;
  play.players.forEach((p, i) => {
    if (pbIsStaticProp(p.type)) return;
    const d = Math.hypot(p.x - start.x, p.y - start.y);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
}

/**
 * The actor for every line at once.
 *
 * Proximity alone is not enough. The motion tool snaps a route's first point to
 * the end of the motion line that precedes it, so the route begins yards from
 * where its player is standing and binds to nobody. A second pass therefore
 * hands an unbound line to whoever runs the line it continues — the endpoints
 * coincide, which is exactly what the snap guaranteed — and repeats until no
 * more chains form, so motion → route → route all belong to one player.
 */
export function resolveActors(play: PlayData): number[] {
  const actors = play.lines.map((_, i) => lineActor(play, i));
  const endOf = (i: number): XY | null => {
    const pts = play.lines[i]?.points;
    return pts && pts.length >= 2 ? pts[pts.length - 1]! : null;
  };
  for (let pass = 0; pass < play.lines.length; pass++) {
    let changed = false;
    play.lines.forEach((line, i) => {
      if (actors[i]! >= 0 || !line?.points || line.points.length < 2) return;
      const start = line.points[0]!;
      for (let j = 0; j < play.lines.length; j++) {
        if (j === i || actors[j]! < 0) continue;
        const end = endOf(j);
        if (end && Math.hypot(end.x - start.x, end.y - start.y) <= ANIM_JOIN_RADIUS) {
          actors[i] = actors[j]!;
          changed = true;
          return;
        }
      }
    });
    if (!changed) break;
  }
  return actors;
}

function segmentDuration(line: PlayLine): number {
  if (typeof line.dur === 'number' && line.dur > 0) return line.dur;
  const speed = line.style === 'motion' ? ANIM_MOTION_SPEED : ANIM_SPEED;
  return Math.max(ANIM_MIN_DUR, (polylineLength(line.points) / speed) * 1000);
}

/**
 * Work out when each line runs.
 *
 * Two rules produce football-correct timing with no authored data:
 *   1. A player's own lines run in sequence, in the order they were drawn. The
 *      motion tool pushes a motion line then a route line, so a motion man
 *      walks across and then runs his route without anyone saying so.
 *   2. Leading motion happens before the snap, and is back-timed so every
 *      motion *arrives* on the snap. Everything else starts at the snap.
 *
 * An explicit `delay` on a line overrides rule 2 for that line (and everything
 * chained behind it), measured from the snap, so a puller can be given a beat.
 */
export function buildTimeline(play: PlayData | null | undefined): Timeline {
  if (!play || !play.lines?.length) return { segments: [], snap: 0, duration: 0 };

  const chains = new Map<number, number[]>();
  const loose: number[] = [];
  const actors = resolveActors(play);
  play.lines.forEach((line, i) => {
    if (!line?.points || line.points.length < 2) return;
    const actor = actors[i]!;
    if (actor < 0) loose.push(i);
    else {
      if (!chains.has(actor)) chains.set(actor, []);
      chains.get(actor)!.push(i);
    }
  });

  // How long each chain spends in pre-snap motion.
  const preSnapOf = (idxs: number[]): number => {
    let ms = 0;
    for (const i of idxs) {
      const line = play.lines[i]!;
      if (line.style !== 'motion' || typeof line.delay === 'number') break;
      ms += segmentDuration(line);
    }
    return ms;
  };
  const snap = Math.max(0, ...[...chains.values()].map(preSnapOf));

  const segments: AnimSegment[] = [];
  const emit = (idxs: number[], actor: number) => {
    const preSnapMs = actor < 0 ? 0 : preSnapOf(idxs);
    let cursor = snap - preSnapMs;
    let stillPreSnap = preSnapMs > 0;
    for (const i of idxs) {
      const line = play.lines[i]!;
      const dur = segmentDuration(line);
      const motionHead = stillPreSnap && line.style === 'motion' && typeof line.delay !== 'number';
      if (!motionHead) {
        stillPreSnap = false;
        if (typeof line.delay === 'number') cursor = snap + line.delay;
        else if (cursor < snap) cursor = snap;
      }
      segments.push({ line: i, actor, start: cursor, dur, preSnap: motionHead });
      cursor += dur;
    }
  };
  for (const [actor, idxs] of chains) emit(idxs, actor);
  for (const i of loose) emit([i], -1);

  segments.sort((a, b) => a.start - b.start || a.line - b.line);
  const duration = segments.reduce((m, s) => Math.max(m, s.start + s.dur), 0);
  return { segments, snap, duration };
}

/** Total run time including the pause on the final frame. */
export function animDuration(play: PlayData | null | undefined): number {
  const tl = buildTimeline(play);
  return tl.duration === 0 ? 0 : tl.duration + ANIM_TAIL_MS;
}

// ─── The frame ──────────────────────────────────────────────────────────────

/**
 * The play as it looks `t` ms in: lines trimmed to how far they have been run,
 * and each line's player moved to the head of it. Pure — `play` is not touched,
 * and `playAtTime(play, animDuration(play))` is the fully-drawn diagram again
 * except that runners stand at the end of their routes.
 */
export function playAtTime(play: PlayData, t: number, timeline?: Timeline): PlayData {
  const tl = timeline ?? buildTimeline(play);
  if (tl.segments.length === 0) return play;

  const lines: PlayLine[] = play.lines.map((l) => ({ ...l, points: [] }));
  const moved = new Map<number, XY>();
  // Latest segment each actor has reached, so a player sits at the head of the
  // line they are running rather than snapping back to an earlier one.
  const reached = new Map<number, number>();

  for (const seg of tl.segments) {
    const src = play.lines[seg.line]!;
    const total = polylineLength(src.points);
    const p = seg.dur <= 0 ? 1 : Math.max(0, Math.min(1, (t - seg.start) / seg.dur));
    lines[seg.line] = { ...src, points: trimPolyline(src.points, total * p) };
    if (seg.actor < 0 || p <= 0) continue;
    if ((reached.get(seg.actor) ?? -Infinity) > seg.start) continue;
    reached.set(seg.actor, seg.start);
    moved.set(seg.actor, pointAtDistance(src.points, total * p));
  }

  const players: PlayPlayer[] = play.players.map((pl, i) => {
    const at = moved.get(i);
    return at ? { ...pl, x: at.x, y: at.y } : pl;
  });

  return { ...play, players, lines };
}

// ─── Playback: look at the play, then watch it run ─────────────────────────

export interface PlaybackFrame {
  /** What to draw. Its lines are the FULL paths while they are still visible. */
  play: PlayData;
  /** Opacity for the lines (1 → 0 over ANIM_FADE_MS; 0 means none are drawn). */
  lineAlpha: number;
  /** Milliseconds into the movement itself (negative during the hold). */
  moveT: number;
}

/** Length of one playback loop: hold + movement + tail. 0 when nothing moves. */
export function playbackDuration(play: PlayData | null | undefined): number {
  const d = animDuration(play);
  return d === 0 ? 0 : ANIM_HOLD_MS + d;
}

/**
 * The frame the player shows at `t` ms of playback. For ANIM_HOLD_MS the whole
 * diagram sits still, so a coach reads the play first; then the players run and
 * the full paths fade out over ANIM_FADE_MS, leaving just the players moving.
 * (Chosen by Mike over "paths draw in behind each runner", Sep 25 2026.)
 * Pure, like playAtTime: the stored play is only read.
 */
export function playbackFrame(play: PlayData, t: number, timeline?: Timeline): PlaybackFrame {
  const tl = timeline ?? buildTimeline(play);
  if (tl.segments.length === 0 || t < ANIM_HOLD_MS) return { play, lineAlpha: 1, moveT: t - ANIM_HOLD_MS };
  const moveT = t - ANIM_HOLD_MS;
  const moved = playAtTime(play, moveT, tl);
  const lineAlpha = Math.max(0, 1 - moveT / ANIM_FADE_MS);
  return { play: { ...moved, lines: lineAlpha > 0 ? play.lines : [] }, lineAlpha, moveT };
}
